import { useState, useRef, useEffect } from "react";
import {
  Sparkles,
  ArrowRight,
  Clock3,
  Store,
  Zap,
  Star,
} from "lucide-react";
import type { DemoFormData } from "./demoTypes";
import DemoWizard from "./components/DemoWizard";
import DemoProcessing from "./components/DemoProcessing";
import DemoPreview from "./components/DemoPreview";
import DemoSimulator from "./components/DemoSimulator";
import ReadyExamplesScreen from "./components/ReadyExamplesScreen";
import type { DemoFlow } from "./flowTypes";
import { buildDemoFlow } from "./flowBuilder";
import { readyPresetToFormData, getReadyExamplePreset } from "./readyExamples";
import { initSmartDemoTracking, trackDemoEvent, setDemoMode, beginAttempt } from "./smartDemoTracking";

function revokeObjectUrls(data: DemoFormData | null) {
  if (!data) return;
  const categoryUrls = data.serviceCategories.flatMap((c) => c.images);
  const urls = [data.logo, ...data.gorseller, ...data.videolar, ...categoryUrls].filter(
    (u): u is string => typeof u === "string" && u.startsWith("blob:"),
  );
  urls.forEach((u) => URL.revokeObjectURL(u));
}

type DemoMode = "custom" | "ready" | "live";
type Stage =
  | "landing"
  | "wizard"
  | "processing"
  | "preview"
  | "simulator"
  | "ready-examples";

export default function DemoPlatform() {
  const [stage, setStage] = useState<Stage>("landing");
  const [mode, setMode] = useState<DemoMode>("custom");
  const [formData, setFormData] = useState<DemoFormData | null>(null);
  const [demoFlow, setDemoFlow] = useState<DemoFlow | null>(null);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  const formDataRef = useRef<DemoFormData | null>(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    return () => revokeObjectUrls(formDataRef.current);
  }, []);

  useEffect(() => {
    initSmartDemoTracking();
    trackDemoEvent("demo:opened", "demo_opened", { source: "smart_demo_web" });
  }, []);

  const clearDemo = () => {
    revokeObjectUrls(formData);
    setFormData(null);
    setDemoFlow(null);
    setActivePresetId(null);
  };

  const startCustom = () => {
    clearDemo();
    setMode("custom");
    setDemoMode("custom");
    beginAttempt();
    trackDemoEvent("mode:custom", "mode_selected_custom", { mode: "custom" });
    setStage("wizard");
  };

  const startReady = () => {
    clearDemo();
    setMode("ready");
    setDemoMode("ready");
    setStage("ready-examples");
  };

  const handleComplete = (data: DemoFormData) => {
    setFormData(data);
    setDemoFlow(buildDemoFlow(data));
    setStage("processing");
  };

  const selectReadyPreset = (presetId: string) => {
    const preset = getReadyExamplePreset(presetId);
    if (!preset || !preset.enabled) return;
    setDemoMode("ready");
    beginAttempt();
    trackDemoEvent("mode:ready", "mode_selected_ready", {
      mode: "ready",
      preset_id: preset.id,
      sector: preset.sectorId,
      preset_name: preset.businessName,
    });
    const data = readyPresetToFormData(preset);
    setActivePresetId(presetId);
    setFormData(data);
    setDemoFlow(buildDemoFlow(data));
    setStage("simulator");
  };

  const resetToLanding = () => {
    clearDemo();
    setMode("custom");
    setStage("landing");
  };

  const backToReadyExamples = () => {
    setActivePresetId(null);
    setStage("ready-examples");
  };

  return (
    <div className="min-h-screen">
      {stage === "landing" && (
        <LandingScreen onCustom={startCustom} onReady={startReady} />
      )}
      {stage === "wizard" && (
        <DemoWizard onComplete={handleComplete} onExit={resetToLanding} />
      )}
      {stage === "processing" && formData && (
        <DemoProcessing data={formData} onDone={() => setStage("preview")} />
      )}
      {stage === "preview" && formData && demoFlow && (
        <DemoPreview
          data={formData}
          flow={demoFlow}
          onStart={() => setStage("simulator")}
          onReset={resetToLanding}
        />
      )}
      {stage === "simulator" && formData && demoFlow && (
        mode === "ready" ? (
          <DemoSimulator
            data={formData}
            flow={demoFlow}
            onEdit={backToReadyExamples}
            editLabel="Başka Bir Örnek Seç"
            completionEditLabel="Başka Bir Örnek Seç"
            onCreateCustom={startCustom}
          />
        ) : (
          <DemoSimulator
            data={formData}
            flow={demoFlow}
            onEdit={() => setStage("preview")}
            editLabel="Demoyu Düzenle"
            completionEditLabel="Bilgilerimi Düzenle"
          />
        )
      )}
      {stage === "ready-examples" && (
        <ReadyExamplesScreen onBack={resetToLanding} onSelect={selectReadyPreset} />
      )}
    </div>
  );
}

function LandingScreen({
  onCustom,
  onReady,
}: {
  onCustom: () => void;
  onReady: () => void;
}) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-primary-950 text-background-50">
      {/* Arka plan ışıkları */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[620px] w-[620px] rounded-full bg-accent-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 h-[540px] w-[540px] rounded-full bg-primary-500/30 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center px-6 py-16 text-center">
        {/* Brand */}
        <div className="animate-fade-in-up flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-primary-950">
            <Sparkles className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight">SMART DEMO</span>
        </div>

        {/* Headline */}
        <h1 className="animate-fade-in-up mt-8 max-w-3xl font-heading text-3xl font-extrabold leading-[1.15] tracking-tight md:text-5xl">
          İşletmenizin Akıllı Müşteri Deneyimini Oluşturun
        </h1>

        <p className="animate-fade-in-up mt-6 font-heading text-lg font-semibold text-accent-300 md:text-xl">
          Nasıl denemek istersiniz?
        </p>

        {/* 2 yollu giriş kartları */}
        <div className="mt-10 grid w-full gap-4 md:grid-cols-2">
          {/* 1) Kendi Firmamla Dene — PRIMARY */}
          <div className="animate-fade-in-up flex flex-col justify-between rounded-2xl bg-accent-500 p-7 text-left text-primary-950 md:p-9">
            <div>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-950/15 px-3 py-1 text-xs font-bold">
                <Star className="h-3.5 w-3.5" strokeWidth={2.5} />
                En Kişisel Deneyim
              </span>
              <div className="mt-5 flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-950/15">
                  <Store className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h2 className="font-heading text-2xl font-extrabold leading-tight md:text-3xl">
                  Kendi Firmamla Dene
                </h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-primary-900/90 md:text-base">
                Firma bilgilerinizi, hizmetlerinizi ve görsellerinizi ekleyin. İşletmenize özel
                müşteri deneyimini oluşturun.
              </p>
            </div>
            <div className="mt-6">
              <button
                onClick={onCustom}
                className="group flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-950 px-6 py-4 text-base font-bold text-background-50 transition hover:bg-primary-900"
              >
                Kendi Demomu Oluştur
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <span className="mt-3 flex items-center justify-center gap-2 text-sm font-medium text-primary-900/80">
                <Clock3 className="h-4 w-4" />
                Yaklaşık 2 dakika
              </span>
            </div>
          </div>

          {/* 2) Hazır Örnekle Hemen Dene */}
          <div className="animate-fade-in-up flex flex-col justify-between rounded-2xl border border-background-700 bg-background-900/40 p-7 text-left md:p-9">
            <div>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500/15 px-3 py-1 text-xs font-bold text-accent-300">
                <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                Hızlı Demo
              </span>
              <div className="mt-5 flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent-500/15 text-accent-300">
                  <Zap className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h2 className="font-heading text-2xl font-extrabold leading-tight md:text-3xl">
                  Hazır Örnekle Hemen Dene
                </h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-background-300 md:text-base">
                Hiçbir bilgi veya görsel yüklemeden sektörünüze uygun hazır bir örneği hemen
                deneyin.
              </p>
            </div>
            <div className="mt-6">
              <button
                onClick={onReady}
                className="group flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-700 bg-background-800/50 px-6 py-4 text-base font-semibold text-background-100 transition hover:border-accent-400 hover:text-accent-300"
              >
                Hazır Örnekleri Gör
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <span className="mt-3 flex items-center justify-center gap-2 text-sm text-background-400">
                <Clock3 className="h-4 w-4" />
                Yaklaşık 30 saniye
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}